import { useEffect, useState } from "react";
import Spinner from "../ui/Spinner";
import masterService from "../../services/masterEntitiesService";
import { resolveBackendUrl } from "../../services/api";
import { toast } from "react-toastify";
import { Truck, Car, CheckCircle2, XCircle } from "lucide-react";
import styles from "./PropertyNetwork.module.css";

export default function PropertyDeliveryCabNetwork({ societyId }) {
  const [loading, setLoading] = useState(true);
  const [deliveryNetwork, setDeliveryNetwork] = useState([]);
  const [cabNetwork, setCabNetwork] = useState([]);

  const loadNetworks = async () => {
    setLoading(true);
    try {
      const [delData, cabData] = await Promise.all([
        masterService.getPropertyDeliveryNetwork(societyId),
        masterService.getPropertyCabNetwork(societyId)
      ]);
      setDeliveryNetwork(delData);
      setCabNetwork(cabData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load property networks.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (societyId) {
      loadNetworks();
    }
  }, [societyId]);

  const toggleDelivery = async (item) => {
    const newStatus = !item.is_enabled;
    try {
      await masterService.togglePropertyDelivery(societyId, item.delivery_company_id, newStatus);
      setDeliveryNetwork((prev) =>
        prev.map((d) => (d.id === item.id ? { ...d, is_enabled: newStatus } : d))
      );
      toast.success(`${item.delivery_company?.name} ${newStatus ? "Enabled" : "Disabled"}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update delivery network.");
    }
  };

  const toggleCab = async (item) => {
    const newStatus = !item.is_enabled;
    try {
      await masterService.togglePropertyCab(societyId, item.cab_provider_id, newStatus);
      setCabNetwork((prev) =>
        prev.map((c) => (c.id === item.id ? { ...c, is_enabled: newStatus } : c))
      );
      toast.success(`${item.cab_provider?.name} ${newStatus ? "Enabled" : "Disabled"}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update cab network.");
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 1. Delivery Companies Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.iconCircle} style={{ background: "#eff6ff", color: "var(--color-dark-bg)" }}>
            <Truck size={20} />
          </div>
          <div>
            <h3 className={styles.sectionTitle}>Allowed Delivery Services</h3>
            <p className={styles.sectionSubtitle}>
              Toggle which delivery and parcel carriers visitors and couriers can select for this property.
            </p>
          </div>
        </div>

        <div className={styles.networkGrid}>
          {deliveryNetwork.map((d) => (
            <div
              key={d.id}
              className={`${styles.networkCard} ${d.is_enabled ? styles.cardEnabled : styles.cardDisabled}`}
              onClick={() => toggleDelivery(d)}
            >
              <div className={styles.cardLeft}>
                <div className={styles.logoWrap}>
                  {d.delivery_company?.logo_url ? (
                    <img
                      src={resolveBackendUrl(d.delivery_company.logo_url)}
                      alt={d.delivery_company.name}
                      className={styles.logoImg}
                    />
                  ) : (
                    <Truck size={20} color="#94a3b8" />
                  )}
                </div>
                <div>
                  <div className={styles.companyName}>{d.delivery_company?.name}</div>
                  <span className={styles.categoryBadge}>{d.delivery_company?.category}</span>
                </div>
              </div>

              <div className={styles.switch}>
                {d.is_enabled ? (
                  <span className={styles.statusOn}>
                    <CheckCircle2 size={16} /> Enabled
                  </span>
                ) : (
                  <span className={styles.statusOff}>
                    <XCircle size={16} /> Disabled
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 2. Cab Providers Section */}
      <div className={styles.section} style={{ marginTop: "32px" }}>
        <div className={styles.sectionHeader}>
          <div className={styles.iconCircle} style={{ background: "#ecfdf5", color: "#059669" }}>
            <Car size={20} />
          </div>
          <div>
            <h3 className={styles.sectionTitle}>Allowed Cab & Ride-Hailing Providers</h3>
            <p className={styles.sectionSubtitle}>
              Control which taxi networks and auto services are authorized for drop-offs and pick-ups.
            </p>
          </div>
        </div>

        <div className={styles.networkGrid}>
          {cabNetwork.map((c) => (
            <div
              key={c.id}
              className={`${styles.networkCard} ${c.is_enabled ? styles.cardEnabled : styles.cardDisabled}`}
              onClick={() => toggleCab(c)}
            >
              <div className={styles.cardLeft}>
                <div className={styles.logoWrap}>
                  {c.cab_provider?.logo_url ? (
                    <img
                      src={resolveBackendUrl(c.cab_provider.logo_url)}
                      alt={c.cab_provider.name}
                      className={styles.logoImg}
                    />
                  ) : (
                    <Car size={20} color="#94a3b8" />
                  )}
                </div>
                <div>
                  <div className={styles.companyName}>{c.cab_provider?.name}</div>
                  <span className={styles.categoryBadge} style={{ color: "#059669", background: "#ecfdf5" }}>
                    Transit
                  </span>
                </div>
              </div>

              <div className={styles.switch}>
                {c.is_enabled ? (
                  <span className={styles.statusOn}>
                    <CheckCircle2 size={16} /> Enabled
                  </span>
                ) : (
                  <span className={styles.statusOff}>
                    <XCircle size={16} /> Disabled
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
